import React, { useMemo, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Play, Thermometer, ShieldAlert, Loader2 } from "lucide-react";

// Technician-side Testing & Quality Check panel.
// Renders during status === "TESTING". Talks to the backend directly for
// step transitions and temperature submissions. State updates arrive back
// via the WS `testing_*` broadcast (lifted into props by parent).
const STEP_LABELS = {
  windows:    "Windows Installation",
  gpu_driver: "GPU Driver Installation",
  bench:      "GPU & CPU Bench Test",
};
const STEP_ORDER = ["windows", "gpu_driver", "bench"];

function StepRow({ id, label, step, onStart, onComplete, busy, starting, completing }) {
  const status = step?.status || "PENDING";
  const running = status === "IN_PROGRESS";
  const done = status === "COMPLETED";
  return (
    <div className="rounded-lg border p-4 flex items-center justify-between gap-3"
         style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
         data-testid={`testing-step-${id}`}>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold">{label}</div>
        <div className="mt-1 text-sm font-semibold" style={{
          color: done ? "#00ff66" : running ? "var(--accent)" : "rgba(255,255,255,0.55)",
        }} data-testid={`testing-step-${id}-status`}>
          {done ? "COMPLETED ✓" : running ? (id === "bench" ? "TESTING…" : "INSTALLING…") : "PENDING"}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!running && !done && (
          <button type="button" onClick={onStart} disabled={busy || starting}
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide border transition-colors disabled:opacity-40"
            style={{ borderColor: "rgba(0,229,255,0.5)", color: "var(--accent)", background: "rgba(0,229,255,0.06)" }}
            data-testid={`testing-step-${id}-start`}>
            {starting ? <Loader2 size={12} className="inline mr-1 animate-spin"/> : <Play size={12} className="inline mr-1"/>}
            {starting ? "STARTING…" : "START"}
          </button>
        )}
        {running && (
          <button type="button" onClick={onComplete} disabled={busy || completing}
            className="px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide border transition-colors disabled:opacity-40"
            style={{ borderColor: "rgba(0,255,102,0.5)", color: "#00ff66", background: "rgba(0,255,102,0.06)" }}
            data-testid={`testing-step-${id}-complete`}>
            {completing ? <Loader2 size={12} className="inline mr-1 animate-spin"/> : <CheckCircle2 size={12} className="inline mr-1"/>}
            {completing ? "COMPLETING…" : "COMPLETE"}
          </button>
        )}
        {done && <CheckCircle2 size={20} style={{ color: "#00ff66" }}/>}
      </div>
    </div>
  );
}

function TempRow({ kind, label, temp, standards, onSubmit, busy }) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const expected = standards?.[kind]?.expected;
  const current = temp && temp.value !== null ? temp : null;
  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) { toast.error("Enter a numeric °C"); return; }
    if (n < 0 || n > 200) { toast.error("Value must be 0-200°C"); return; }
    setSubmitting(true);
    try { await onSubmit(kind, n); setValue(""); }
    catch { /* onSubmit surfaces the error */ }
    finally { setSubmitting(false); }
  };
  const sevColor = { success: "#00ff66", normal: "var(--accent)", warning: "#ff9800", critical: "#ff3333" }[current?.severity] || "rgba(255,255,255,0.55)";
  const isCritical = current?.classification === "TEMP_TOO_HIGH";

  return (
    <div className="rounded-lg border p-4"
         style={{ borderColor: isCritical ? "rgba(255,51,51,0.45)" : "rgba(255,255,255,0.08)",
                  background: isCritical ? "rgba(255,51,51,0.06)" : "rgba(255,255,255,0.02)" }}
         data-testid={`testing-temp-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold flex items-center gap-1.5">
            <Thermometer size={11}/> {label}
          </div>
          {expected && (
            <div className="mt-0.5 text-[10px] text-white/40 tracking-wide" data-testid={`testing-temp-${kind}-expected`}>
              Expected Range: {expected.min}–{expected.max}°C
            </div>
          )}
          {current ? (
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white" data-testid={`testing-temp-${kind}-value`}>{current.value}°C</span>
              <span className="text-xs font-semibold tracking-wide" style={{ color: sevColor }} data-testid={`testing-temp-${kind}-class`}>
                {current.classification?.replace(/_/g, " ")} {current.severity === "success" ? "✓" : current.severity === "critical" ? "⚠" : ""}
              </span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-white/40 italic">Awaiting reading…</div>
          )}
        </div>
      </div>
      <form onSubmit={submit} className="mt-3 flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          placeholder={current ? "Re-submit °C" : "°C"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-20 px-2 py-1.5 rounded-md text-sm bg-transparent border text-white"
          style={{ borderColor: "rgba(255,255,255,0.15)" }}
          data-testid={`testing-temp-${kind}-input`}
          min={0}
          max={200}
        />
        <button type="submit" disabled={busy || submitting}
          className="px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide border transition-colors disabled:opacity-40"
          style={{ borderColor: "rgba(0,229,255,0.5)", color: "var(--accent)", background: "rgba(0,229,255,0.06)" }}
          data-testid={`testing-temp-${kind}-submit`}>
          {submitting ? <Loader2 size={12} className="inline animate-spin mr-1"/> : null}
          {current ? "RE-SUBMIT" : "SUBMIT"}
        </button>
        {isCritical && (
          <span className="text-[10px] font-semibold text-red-400 tracking-wide flex items-center gap-1 ml-1" data-testid={`testing-temp-${kind}-critical-hint`}>
            <ShieldAlert size={11}/> Re-check cooling & re-measure
          </span>
        )}
      </form>
    </div>
  );
}

export function TechnicianTestingPanel({ orderId, testing, onCompleteTesting, onTestingUpdate, busy }) {
  const [busyStep, setBusyStep] = useState(null); // e.g. `windows:complete` — prevents double-clicks per-button
  const [busyTemp, setBusyTemp] = useState(null); // "gpu" | "cpu"
  const standards = testing?.standards_snapshot;

  const canComplete = useMemo(() => {
    if (!testing) return false;
    const stepsOk = STEP_ORDER.every((s) => (testing.steps?.[s]?.status) === "COMPLETED");
    const gpuOk = testing.gpu_temp?.value !== null && testing.gpu_temp?.value !== undefined && testing.gpu_temp?.classification !== "TEMP_TOO_HIGH";
    const cpuOk = testing.cpu_temp?.value !== null && testing.cpu_temp?.value !== undefined && testing.cpu_temp?.classification !== "TEMP_TOO_HIGH";
    return stepsOk && gpuOk && cpuOk;
  }, [testing]);

  const setStep = async (step, action) => {
    const key = `${step}:${action}`;
    if (busyStep) return; // hard guard against duplicate PUTs
    setBusyStep(key);
    try {
      const { data } = await client.put(`/orders/${orderId}/testing/steps/${step}`, { action });
      // Update local state IMMEDIATELY from the authoritative server response.
      // (Don't wait for the WS broadcast — self-echo can be lossy and the
      // periodic parent poll doesn't re-fetch testing state on same-status.)
      if (data && onTestingUpdate) onTestingUpdate(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.reason || `${action} failed`);
      toast.error(`${step.replace("_", " ")}: ${msg}`);
      // eslint-disable-next-line no-console
      console.error("testing step", { step, action, status: e.response?.status, data: e.response?.data });
    } finally {
      setBusyStep(null);
    }
  };

  const submitTemp = async (kind, value) => {
    if (busyTemp) return;
    setBusyTemp(kind);
    try {
      const { data } = await client.post(`/orders/${orderId}/testing/temperature`, { kind, value });
      if (data && onTestingUpdate) onTestingUpdate(data);
      const cls = data?.[`${kind}_temp`]?.classification;
      if (cls === "TEMP_TOO_HIGH") toast.error(`${kind.toUpperCase()} temperature TOO HIGH — please re-check.`);
      else toast.success(`${kind.toUpperCase()}: ${value}°C recorded`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
      throw e;
    } finally { setBusyTemp(null); }
  };

  return (
    <div className="rounded-xl border p-5 md:p-6 space-y-4"
         style={{ borderColor: "rgba(0,229,255,0.22)", background: "linear-gradient(180deg,rgba(0,229,255,0.03),rgba(255,255,255,0.02))" }}
         data-testid="technician-testing-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-semibold" style={{ color: "var(--accent)" }}>Testing & Quality Check</div>
          <div className="text-white/50 text-xs mt-1">Livestream stays active — point the camera at the monitor.</div>
        </div>
      </div>

      <div className="space-y-2">
        {STEP_ORDER.map((s) => {
          const starting  = busyStep === `${s}:start`;
          const completing = busyStep === `${s}:complete`;
          return (
            <StepRow key={s} id={s} label={STEP_LABELS[s]}
              step={testing?.steps?.[s]}
              onStart={() => setStep(s, "start")}
              onComplete={() => setStep(s, "complete")}
              busy={busy || !!busyStep}
              starting={starting}
              completing={completing}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TempRow kind="gpu" label="GPU Temperature" temp={testing?.gpu_temp} standards={standards} onSubmit={submitTemp} busy={busy || busyTemp === "gpu"}/>
        <TempRow kind="cpu" label="CPU Temperature" temp={testing?.cpu_temp} standards={standards} onSubmit={submitTemp} busy={busy || busyTemp === "cpu"}/>
      </div>

      <button type="button" onClick={onCompleteTesting} disabled={!canComplete || busy || !!busyStep || !!busyTemp}
        className="w-full rounded-lg border h-14 flex items-center justify-center gap-2 font-semibold tracking-wide transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        style={{
          borderColor: canComplete ? "rgba(0,255,102,0.55)" : "rgba(255,255,255,0.15)",
          color: canComplete ? "#00ff66" : "rgba(255,255,255,0.4)",
          background: canComplete ? "rgba(0,255,102,0.06)" : "transparent",
        }}
        data-testid="complete-testing-button">
        <CheckCircle2 size={18}/> COMPLETE TESTING
      </button>
    </div>
  );
}
